import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Compass, ShieldCheck, Eye } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import PropertyCard from "../components/PropertyCard";
import { fetchFeaturedProperties } from "../lib/api";

const HERO_BG =
  "https://customer-assets.emergentagent.com/wingman/2aaf26c1-2a07-4709-9e4e-abab64ebc2fe/attachments/51f941ecb55e42ef91b37feff4259830_image.bin";
const EXPERIENCE_IMG =
  "https://customer-assets.emergentagent.com/wingman/2aaf26c1-2a07-4709-9e4e-abab64ebc2fe/attachments/90ea666f9b7141afa67e61c7d872f198_image.bin";

const REVIEWS = [
  {
    text: "Wat een fijne mensen zijn dit. Eindelijk iemand die te vertrouwen is en ook je echt helpen. Na aankoop van ons appartement wat vlekkeloos verliep hebben we nog altijd een leuk contact. Top geregeld Michael en Nancy! Dikke 10.",
    author: "Akko van Oss",
    location: "Nederland",
  },
  {
    text: "Super Professionell, top Auswahl und ausgesprochen Sympathisch! Best Service in Marbella! Gratulation.",
    author: "Anita Lischke",
    location: "Deutschland",
  },
  {
    text: "Professional services tailored to your dreams, ensuring a smooth process from start to finish. Highly recommended if you want to make your dreams come true without worry.",
    author: "Pavlina Zahradnikova",
    location: "Estepona",
  },
];

export default function Home() {
  const { t } = useLang();
  const [featured, setFeatured] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchFeaturedProperties(3)
      .then((d) => {
        if (mounted && d.ok && d.properties && d.properties.length > 0) {
          setFeatured(d.properties.slice(0, 3));
        }
      })
      .catch(() => {})
      .finally(() => mounted && setLoaded(true));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="bg-charcoal" data-testid="page-home">
      {/* HERO — boho-chic image background with centered NM emblem + headline stacked vertically */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: "#EFE4D1" }}
        data-testid="home-hero"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt="Huis aan de Costa del Sol"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div className="absolute inset-0" style={{ background: "rgba(239, 228, 209, 0.35)" }} />
        </div>

        <div className="relative z-10 max-w-[1480px] mx-auto px-6 lg:px-12 pt-32 pb-24 w-full text-center flex flex-col items-center">
          {/* SEO H1 — brand + primary keyword. Small, elegant, gold. */}
          <h1
            className="font-serif text-[0.72rem] md:text-xs tracking-[0.32em] uppercase mb-6"
            style={{ color: "#B8964F" }}
            data-testid="seo-h1"
          >
            Nederlandse Makelaar aan de Costa del Sol | NM Luxury Estates
          </h1>

          {/* Centered gold NM emblem — true transparent PNG above the headline */}
          <img
            src="/nm-logo-gold.png"
            alt="NM Luxury Estates"
            data-testid="hero-nm-emblem"
            className="mb-8 select-none"
            style={{
              width: "clamp(110px, 14vw, 180px)",
              height: "auto",
              background: "transparent",
              filter: "drop-shadow(0 4px 24px rgba(45, 30, 14, 0.25))",
            }}
          />

          <h2
            className="font-serif text-5xl md:text-7xl lg:text-8xl text-cream leading-[1] tracking-tight max-w-5xl mx-auto"
            data-testid="hero-title"
          >
            {t.home.heroTitle}
          </h2>
          <p className="text-cream-muted text-base md:text-lg max-w-2xl mt-8 leading-relaxed font-medium mx-auto">
            {t.home.heroSubtitle}
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/aanbod" className="btn-gold-solid" data-testid="hero-cta-primary">
              {t.home.heroCtaPrimary} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link to="/contact" className="btn-gold-outline" data-testid="hero-cta-secondary">
              {t.home.heroCtaSecondary}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/40 animate-bounce z-10">
          <ChevronDown size={28} strokeWidth={1} />
        </div>
      </section>

      {/* USP */}
      <section className="py-24 md:py-32" data-testid="home-usp">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="overline">{t.home.sectionUspOverline}</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-cream leading-[1.05] tracking-tight mt-5">
                {t.home.sectionUspTitle}
              </h2>
              <span className="divider-gold mt-8" />
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-10">
              {[
                { icon: Compass, ...t.home.uspOne },
                { icon: ShieldCheck, ...t.home.uspTwo },
                { icon: Eye, ...t.home.uspThree },
              ].map((u, i) => {
                const Icon = u.icon;
                return (
                  <div key={i} className="border-t border-white/10 pt-6" data-testid={`usp-${i}`}>
                    <Icon size={22} strokeWidth={1} className="text-gold mb-5" />
                    <h3 className="font-serif text-2xl text-cream mb-3">{u.title}</h3>
                    <p className="text-cream-muted text-sm leading-relaxed">{u.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PROPERTIES — only rendered when live API has real listings */}
      {featured.length > 0 && (
        <section className="py-24 md:py-32 bg-charcoal-surface" data-testid="home-featured">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <p className="overline">{t.home.featuredOverline}</p>
                <h2 className="font-serif text-4xl md:text-5xl text-cream leading-tight mt-3 max-w-2xl">
                  {t.home.featuredTitle}
                </h2>
              </div>
              <Link to="/aanbod" className="btn-gold-outline" data-testid="home-featured-cta">
                {t.home.featuredCta} <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
              {featured.map((p, i) => (
                <PropertyCard property={p} key={p.reference} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOUNDERS — text-only, single column for clean Leon Müller aesthetic */}
      <section className="py-24 md:py-32 pb-56 md:pb-48" data-testid="home-founders">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <p className="overline">{t.home.foundersOverline}</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-cream leading-tight mt-4">
            {t.home.foundersTitle}
          </h2>
          <span className="divider-gold mt-8 mx-auto" />
          <p className="text-cream-muted text-base md:text-lg leading-relaxed mt-10 mx-auto max-w-2xl">
            {t.home.foundersBody}
          </p>
          <div className="mt-12 grid grid-cols-2 gap-8 max-w-md mx-auto">
            <div>
              <p className="font-serif text-xl text-cream">Michael Hannes</p>
              <p className="text-cream-subtle text-xs tracking-[0.18em] uppercase mt-1">Founder</p>
            </div>
            <div>
              <p className="font-serif text-xl text-cream">Nancy Hannes</p>
              <p className="text-cream-subtle text-xs tracking-[0.18em] uppercase mt-1">Co-owner</p>
            </div>
          </div>
          <Link to="/wie-we-zijn" className="btn-gold-outline mt-12 inline-flex" data-testid="home-founders-cta">
            {t.home.foundersCta} <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="pt-32 md:pt-32 pb-40 md:pb-32 bg-charcoal-surface" data-testid="home-experience">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 pl-16 sm:pl-0 pt-12 sm:pt-0">
            <p className="overline">{t.home.experienceOverline}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-cream leading-tight mt-6 sm:mt-3">
              {t.home.experienceTitle}
            </h2>
            <span className="divider-gold mt-7" />
            <p className="text-cream-muted text-base leading-relaxed mt-8 max-w-xl">
              {t.home.experienceBody}
            </p>
            <Link to="/diensten#viewing-experience" className="btn-gold-solid mt-10" data-testid="home-experience-cta">
              {t.home.experienceCta} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="order-1 lg:order-2">
            <img
              src={EXPERIENCE_IMG}
              alt="Persoonlijke Viewing Experience"
              className="block w-full h-auto"
            />
            <p
              className="text-center font-serif font-black not-italic text-base md:text-lg tracking-wide mt-6"
              style={{ color: "#B8964F" }}
              data-testid="experience-caption"
            >
              {t.home.experienceCaption}
            </p>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 md:py-32" data-testid="home-reviews">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          <p className="overline text-center">{t.home.reviewsOverline}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-cream leading-tight text-center mt-3 mb-20">
            {t.home.reviewsTitle}
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {REVIEWS.map((r, i) => (
              <figure
                key={i}
                className="border border-white/10 p-8 flex flex-col"
                data-testid={`review-${i}`}
              >
                <p className="text-gold tracking-[0.4em] mb-4">★★★★★</p>
                <blockquote className="text-cream-muted text-sm leading-relaxed flex-1 font-light italic">
                  "{r.text}"
                </blockquote>
                <figcaption className="mt-6">
                  <p className="text-cream font-medium text-sm">{r.author}</p>
                  <p className="text-cream-subtle text-xs">{r.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-charcoal-deep" data-testid="home-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-6xl text-cream leading-tight">
            {t.home.ctaTitle}
          </h2>
          <p className="text-cream-muted text-base md:text-lg mt-6 leading-relaxed">
            {t.home.ctaBody}
          </p>
          <Link to="/contact" className="btn-gold-solid mt-10 inline-flex" data-testid="home-cta-primary">
            {t.home.ctaPrimary} <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
